/**
 * Email templates — admin-editable transactional email registry.
 *
 * Every notification the Cloud Functions package sends (functions/index.js,
 * via functions/emailTemplates/) renders from `emailTemplates/{id}` when a doc
 * exists and is enabled, otherwise from the code-baked registry default. This
 * module surfaces the generated defaults (single source of truth:
 * functions/emailTemplates/registry.js) to the admin UI and provides the
 * helpers the page needs: default lookup, "customised vs default" detection,
 * effective-content resolution, and a save-time HTML sanitiser.
 */

import { EMAIL_TEMPLATE_DEFAULTS } from './emailTemplateDefaults';

export const EMAIL_TEMPLATES_COLLECTION = 'emailTemplates';
export const EMAIL_TEMPLATE_VERSIONS_SUBCOLLECTION = 'versions';

export const EMAIL_AUDIENCE_LABELS = Object.freeze({
  customer: 'Customer',
  admin: 'Admin',
});

export const EMAIL_CATEGORY_LABELS = Object.freeze({
  property_lifecycle: 'Property lifecycle',
  accounts: 'Accounts',
  security: 'Security',
  billing: 'Billing',
  sales: 'Sales',
});

/** Ordered list of default catalogue entries (stable display order). */
export const EMAIL_TEMPLATE_LIST = EMAIL_TEMPLATE_DEFAULTS;

const DEFAULTS_BY_ID = EMAIL_TEMPLATE_DEFAULTS.reduce((acc, t) => {
  acc[t.id] = t;
  return acc;
}, {});

export function getEmailTemplateDefault(id) {
  return DEFAULTS_BY_ID[id] || null;
}

function isNonEmptyString(v) {
  return typeof v === 'string' && v.trim().length > 0;
}

/**
 * True when a stored doc carries a subject or body that differs from the
 * code-baked default (i.e. an admin has actually changed something).
 */
export function isCustomised(storedData, def) {
  if (!storedData || !def) return false;
  const subjectChanged =
    isNonEmptyString(storedData.subject) && storedData.subject !== def.defaultSubject;
  const htmlChanged = isNonEmptyString(storedData.html) && storedData.html !== def.defaultHtml;
  return Boolean(subjectChanged || htmlChanged);
}

function tsToIso(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value?.toDate === 'function') {
    try { return value.toDate().toISOString(); } catch { return null; }
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return null;
}

/**
 * Merge a code-baked default with an optional stored Firestore doc into a
 * single row the list + editor render against. `state` is one of:
 *   - 'default' — no overrides saved; the default content is live.
 *   - 'custom'  — overrides saved and enabled; custom content is live.
 *   - 'paused'  — overrides saved but disabled; the default content is live.
 * `effectiveSubject` / `effectiveHtml` are what would actually be sent.
 */
export function resolveEmailTemplate(id, storedData) {
  const def = getEmailTemplateDefault(id);
  if (!def) return null;

  const storedExists = !!storedData;
  const enabled = storedExists ? storedData.enabled !== false : false;
  const customised = isCustomised(storedData, def);

  let state = 'default';
  if (customised && enabled) state = 'custom';
  else if (customised && !enabled) state = 'paused';

  const useCustom = state === 'custom';
  const effectiveSubject = useCustom && isNonEmptyString(storedData.subject)
    ? storedData.subject
    : def.defaultSubject;
  const effectiveHtml = useCustom && isNonEmptyString(storedData.html)
    ? storedData.html
    : def.defaultHtml;

  return {
    id,
    audience: def.audience,
    category: def.category,
    displayName: def.displayName,
    description: def.description,
    variableSchema: def.variableSchema || [],
    sampleData: def.sampleData || {},
    defaultSubject: def.defaultSubject,
    defaultHtml: def.defaultHtml,

    storedExists,
    enabled,
    customised,
    state,

    // Draft fields for the editor (fall back to default so the editor never
    // opens with an empty body).
    subject: isNonEmptyString(storedData?.subject) ? storedData.subject : def.defaultSubject,
    html: isNonEmptyString(storedData?.html) ? storedData.html : def.defaultHtml,

    effectiveSubject,
    effectiveHtml,

    version: Number(storedData?.version) || 0,
    updatedAt: tsToIso(storedData?.updatedAt),
    updatedByEmail: storedData?.updatedByEmail || null,
  };
}

/**
 * Save-time HTML sanitiser. Admin authoring for trusted email clients means
 * we don't need full DOMPurify; this allowlist-minded blacklist strips the
 * obvious injection vectors (scripts, iframes, inline event handlers,
 * javascript: URLs, HTML imports). Keep in sync with the threat model in the
 * design doc's "Sanitisation on save" section.
 */
export function sanitizeEmailHtml(html) {
  if (typeof html !== 'string') return '';
  return html
    .replace(/<\s*script[\s\S]*?<\s*\/\s*script\s*>/gi, '')
    .replace(/<\s*script\b[^>]*\/?\s*>/gi, '')
    .replace(/<\s*iframe[\s\S]*?<\s*\/\s*iframe\s*>/gi, '')
    .replace(/<\s*iframe\b[^>]*\/?\s*>/gi, '')
    .replace(/<\s*link\b[^>]*rel\s*=\s*["']?import["']?[^>]*>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/\son\w+\s*=\s*[^\s>]+/gi, '')
    .replace(/(href|src)\s*=\s*("|')\s*javascript:[^"']*\2/gi, '$1=$2#$2');
}
